import { toast } from "@gigpro/ui/Toast";
import { pluralize } from "@gigpro/utils";
import {
  type AsyncDirectOfferResponse,
  type CustomValidationError,
  type DirectOfferV2Request,
  getNextPageParam,
  type StafferGigApplication,
  stafferGigsterProfilesList,
} from "@sidegigllc/api/client";
import { type InfiniteData, type UseInfiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import type { PaginationOptions } from "./types";

type DirectOfferPage = Awaited<ReturnType<typeof stafferGigsterProfilesList>>;

type DirectOfferCandidatesOptions = UseInfiniteQueryOptions<
  DirectOfferPage,
  unknown,
  InfiniteData<DirectOfferPage, number>,
  Array<string | DirectOfferPayload>,
  number
>;

export function directOfferCandidatesQueryOptions(
  params: DirectOfferPayload,
  options?: Omit<DirectOfferCandidatesOptions, "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam">,
): DirectOfferCandidatesOptions {
  return {
    queryKey: ["DIRECT_OFFER_LIST_PROS", params],
    queryFn: ({ pageParam }) =>
      stafferGigsterProfilesList({
        ...params,
        page: pageParam,
        page_size: 20,
        ordering: "review_summary__avg_rating",
      }),
    initialPageParam: 1,
    getNextPageParam,
    ...options,
  };
}

export type DirectOfferPayload = Pick<PaginationOptions, "page_size"> & {
  gig_id: number;
  ordering?: string;
  previous_position?: boolean;
  pro_verified?: boolean;
  favorite_pro?: boolean;
};

export const useDirectOfferCandidates = (...[params, options]: Parameters<typeof directOfferCandidatesQueryOptions>) =>
  useInfiniteQuery(directOfferCandidatesQueryOptions(params, options));

export const getDirectOfferError = (
  result: AsyncDirectOfferResponse | Array<StafferGigApplication> | CustomValidationError | DirectOfferV2Request,
) => ("error" in result ? result.error : undefined);

export const countSyncCreatedOffers = (
  result: AsyncDirectOfferResponse | Array<StafferGigApplication> | CustomValidationError | DirectOfferV2Request,
  proCount: number,
) => {
  if (Array.isArray(result)) return result.length;
  return "gig_id" in result ? proCount : 0;
};

/** The gigs at least one offer went out for, given each gig's own results (a gig may send two requests). */
export const gigsOffered = <Gig>(
  gigs: ReadonlyArray<Gig>,
  perGig: ReadonlyArray<ReadonlyArray<Parameters<typeof countSyncCreatedOffers>[0]>>,
  proCount: number,
) =>
  gigs.filter((_, index) =>
    perGig[index]?.some((result) =>
      "advisory_errors" in result ? result.queued_count > 0 : countSyncCreatedOffers(result, proCount) > 0,
    ),
  );

export const countUniqueMembers = (memberLists: Array<Array<{ id: number }>>) =>
  new Set(memberLists.flatMap((members) => members.map(({ id }) => id))).size;

export const offerSentMessage = (proCount: number) =>
  `${pluralize(proCount, "Offer")} sent, check the shift page for updates on the ${pluralize(proCount, "offer")}`;

/** Renders the success toast for a direct-offer submit, or a warning when nothing was queued.
 *  `advisoryErrorCount` is informational only — the worker re-validates with fresh DB state, so
 *  advisory errors never change the success copy and must not read as "skipped". */
export const showOfferSentToast = (
  proCount: number,
  queuedCount: number,
  advisoryErrorCount: number,
  options?: { id?: string | number },
) => {
  if (queuedCount === 0) {
    toast.warning("All direct offers failed to send", options);
  } else if (advisoryErrorCount === 0) {
    toast.success(offerSentMessage(proCount), options);
  } else {
    toast.success(offerSentMessage(proCount), {
      ...options,
      description: `${advisoryErrorCount} may need review before sending`,
    });
  }
};
